import { css } from '@emotion/css'
import { t } from 'i18next'
import moment from 'moment'
import qs from 'qs'
import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useCookies } from 'react-cookie'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { fetchIntervalUsage, fetchPlans, fetchPremiseDetail, getGreenEnergy, getSaveCarbon, fetchUsage, fetchMonthly } from '../api/api'
import { useUpdateEffect } from '../common/hooks/myHooks'
import CircleProgressBar from '../components/CircleProgressBar'
import Footer from '../components/footer'
import Header from '../components/header'
import { Card } from '../components/styles/MyComponents.styled'
import TextData from '../text-data'
import { PlanProps, PremiseInfo, RenewableProps, SaveCarbonProps } from '../types'
import { ChooseDataSourceModal } from '../components/choose-data-source-modal';
import { LoadingRow } from '../components/loading-row';

const AnalysisReport = () => {
    const months = [t('Jan', { ns: 'common' }), t('Feb', { ns: 'common' }), t('Mar', { ns: 'common' }), t('Apr', { ns: 'common' }), t('May', { ns: 'common' }), t('Jun'),
    t('Jul', { ns: 'common' }), t('Aug', { ns: 'common' }), t('Sep', { ns: 'common' }), t('Oct', { ns: 'common' }), t('Nov', { ns: 'common' }), t('Dec', { ns: 'common' })];

    let { equivalentCards, reduceCards } = TextData.AnalysisData

    const navigate = useNavigate()
    const location = useLocation()

    const [cookies] = useCookies(['consent_id', 'actual_usage']);

    const [consentId, setConsentId] = useState<string>('')
    const [mapAddress, setMapAddress] = useState<string>('')

    const [premiseDetail, setPremiseDetail] = useState<PremiseInfo>({} as PremiseInfo)
    const [renewable, setRenewable] = useState<RenewableProps>({} as RenewableProps)
    const [saveCarbon, setSaveCarbon] = useState<SaveCarbonProps>()
    const [plan, setPlan] = useState<PlanProps>()

    const [chartData, setChartData] = useState([])
    const [emissionData, setEmisstionData] = useState([])
    const [totalUsage, setTotalUsage] = useState<number>()
    const [removal, setRemoval] = useState<number>(0)
    const [maxhourly, setMaxhourly] = useState<number>(0)
    const [showDataSourceModal, setShowDataSourceModal] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const [fetchActualusage, setFetchActualusage] = useState<string>('')


    let { address, city, esiid, zipcode, utcode, state, actualusage } = qs.parse(
        location.search,
        { ignoreQueryPrefix: true }
    )

    function capitalizeString(str: any) {
        return str.split(' ').map((word: any) => word[0].toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    }

    useEffect(() => {
        setChartLoading(true)
        if (!esiid) {
            navigate('/')
            return
        }

        let _mapAddress = `${capitalizeString(address)}, ${capitalizeString(city)}, ${state} ${zipcode}`;
        setMapAddress(_mapAddress)

        setConsentId(cookies.consent_id)
        if (actualusage) {
            usageHandle(actualusage)
        } else {
            getUsage()
        }

    }, [location.key])

    const getUsage = () => {
        // const startDate = moment().subtract(1, 'years').format('YYYY-MM-DD');
        // const endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
        fetchMonthly(undefined, '2023-01-01', '2023-12-31', zipcode).then((res) => {
            if (res && res.response && res.response.reads) {
                const actualUsage = res.response.reads.map((item: any) => Math.round(item.usage)).join('-');
                setFetchActualusage(actualUsage)
                usageHandle(actualUsage)
            }
        })
    }

    const usageHandle = (usageString: any) => {
        const actualUsageParams = usageString.toString().split('-')
        let chartData = [];
        let totalUsage = 0
        for (let i in actualUsageParams) {
            chartData.push({ name: months[parseInt(i)], value: parseInt(actualUsageParams[i]) })
            totalUsage += parseInt(actualUsageParams[i])
        }
        setChartData(chartData as any)
        setTotalUsage(totalUsage)

        fetchPlans('normal',
            actualUsageParams,
            zipcode,
            {
                renewable: true,
                term: [12],
                provider_id: [269]//goodcharlie
            },
            `${utcode}`).then((res) => {
                if (res && res.response && res.response.plans) {
                    setPlan(res.response.plans[0])
                }
            })
    }

    useUpdateEffect(() => {
        if (!zipcode) {
            navigate('/')
        }
        getGreenEnergy(zipcode, consentId).then((res) => {
            setChartLoading(false)
            if (res && res.response) {
                setRenewable(res.response)
            }
        })
        fetchPremiseDetail(zipcode, address).then((res) => {
            if (res.status === 1) {
                setPremiseDetail(res.premise)
            }
        })

        const startDate = moment().subtract(1, 'months').format('MM/DD/YYYY');
        const endDate = moment().subtract(2, 'days').format('MM/DD/YYYY');
        fetchIntervalUsage(consentId, startDate, endDate, undefined).then((res) => {
            if (res && res.response && res.response.reads) {
                const hourlyData = res.response.reads.reduce((result: any, item: any) => {
                    const [date, time] = item.datetime.split(' ');
                    const [hour] = time.split(':');

                    if (result[hour]) {
                        result[hour].usage += parseFloat(item.usage);
                    }
                    else {
                        result[hour] = {
                            usage: parseFloat(item.usage),
                        };
                    }

                    return result;
                }, {});

                const hourlyArray = Object.values(hourlyData).map((item: any) => item.usage);

                const average = hourlyArray.reduce((sum, value) => sum + value, 0) / hourlyArray.length;
                const max = Math.max(...hourlyArray);
                const percentage = (average / max) * 100;

                setRemoval(percentage)

                const maxIndex = hourlyArray.indexOf(max);
                setMaxhourly((maxIndex || 5) + 1)
            }
        })
    }, [totalUsage])

    useUpdateEffect(() => {
        if (!renewable || !totalUsage) {
            return
        }
        getSaveCarbon(zipcode, (totalUsage * (100 - renewable.renewable_content) / 100)).then((res) => {
            if (res && res.data) {
                setSaveCarbon(res.data)
            }
        })
    }, [renewable])

    useUpdateEffect(() => {
        if (!renewable || !renewable.daily) {
            return
        }
        const dailyArray = Object.entries(renewable.daily).map(([date, data]: [any, any]) => ({
            time: date,
            total: Number(Number(data.total).toFixed(2)),
            renewable: Number((data.renewable * parseFloat(data.total) / 100).toFixed(2)),
        }));
        setEmisstionData(dailyArray.splice(0, 25) as any);

    }, [renewable])

    function convertTo12HourFormat(hour: number) {
        if (hour === 0) {
            return '12 AM';
        } else if (hour < 12) {
            return hour + ' AM';
        } else if (hour === 12) {
            return '12 PM';
        } else {
            return (hour - 12) + ' PM';
        }
    }

    const toEnroll = () => {
        if (!plan) {
            return
        }
        localStorage.setItem('ts_plan', JSON.stringify(plan));
        navigate(
            `/enrollment?plan_id=${plan.plan_id}&address=${encodeURIComponent(
                `${address}`
            )}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&utility_code=${utcode}&step=service_date`
        )
    }

    return (
        <>
            <Header />

            <Container className={css`
                transform: translateY(50px);

                @media (max-width: 990px) {
                    transform: translateY(0px);
                    padding-top:30px;
                    padding-bottom:10px;
                }
            `}>
                <Row className={css`
                    @media(max-width:990px){
                        gap:20px;
                    }
                `}>
                    <Col lg={7}>
                        <div className='d-flex gap-3' style={{ height: '100%', marginTop: 18 }}>
                            <div className={css`
                                display: flex;
                                gap: 15px;
                                @media (max-width: 768px) {
                                    display: none;
                                }
                            `}>
                                <img src="/images/icon_house.png" alt="" width={60} height={60} />
                                <div className={css`
                                width: 3px;
                                height: 64px;
                                background-color: #CFE5D6;
                            `} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex gap-5 align-items-center'>
                                    <div className={css`
                                        font-size: 20px;
                                        font-weight: 700;
                                        color: #545151;
                                    `}>{mapAddress}</div>
                                    <button className={css`
                                        border-radius:5px;
                                        color:#1DB461;
                                        background:#fff;
                                        border:1px solid #1DB461;
                                        padding:3px 15px;
                                    `} onClick={() => { navigate('/') }}>Edit</button>
                                </div>
                                <div className='d-flex gap-5 align-items-center mt-2'>
                                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                                        {premiseDetail?.detail?.lot_size
                                            ? Number(premiseDetail.detail.lot_size).toLocaleString()
                                            : 0} sqft
                                    </div>

                                    <div className='d-flex gap-3 me-3'>
                                        <div className='d-flex gap-1'>
                                            <img src="/images/icon_bed.png" alt="" width={22} height={22} />
                                            <p className='mb-0' style={{ fontSize: 16 }}>{premiseDetail?.detail?.bedrooms || 0}</p>
                                        </div>
                                        <div className='d-flex gap-1'>
                                            <img src="/images/icon_bath.png" alt="" width={22} height={22} />
                                            <p className='mb-0' style={{ fontSize: 16 }}>{premiseDetail?.detail?.bathrooms || 0}</p>
                                        </div>
                                    </div>
                                    <button className={css`
                                        border-radius:5px;
                                        background:#1DB461;
                                        color:#fff;
                                        border:1px solid #1DB461;
                                        padding:3px 15px;
                                    `} onClick={() => { setShowDataSourceModal(true) }}>Get My Usage</button>
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col lg={5}>
                        <Card border>
                            <div className='d-flex flex-column'>
                                <div style={{ fontSize: 14, textAlign: 'center' }}>Estimated electricity consumption for the past year</div>
                                <div id="usage-chart">
                                    <ResponsiveContainer width={'99%'} height={130}>
                                        <BarChart
                                            data={chartData}
                                            margin={{
                                                top: 12, right: 24, left: -12, bottom: 0,
                                            }}
                                        >
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" interval={1} />
                                            <YAxis tick={{ fontSize: 11, fill: '#999' }} stroke="#fff" />
                                            <CartesianGrid vertical={false} strokeDasharray="2" />
                                            <Tooltip
                                                itemStyle={{
                                                    fontSize: 13,
                                                    fontWeight: 'bold',
                                                    color: '#fff'
                                                }}
                                                labelStyle={{
                                                    fontSize: 11,
                                                    fontWeight: 'normal',
                                                    color: '#fff'
                                                }}
                                                contentStyle={{
                                                    backgroundColor: 'rgba(0, 0, 0, .6)',
                                                    border: 0,
                                                    borderRadius: 4
                                                }}
                                                cursor={{
                                                    fill: '#f7f7f7'
                                                }}
                                                content={({ active, payload, label }) => {
                                                    if (active && label && payload) {
                                                        return (
                                                            <div className="chart-tooltip">
                                                                <div className="date">{label}</div>
                                                                <div className="value">{payload[0].value + 'kwh'}</div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Bar dataKey="value"
                                                isAnimationActive={true}
                                                radius={3}
                                                maxBarSize={10}
                                            >
                                                {chartData.map((entry, idx) => (
                                                    <Cell key={idx} fill="#FCC068" />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div style={{
                                    fontSize: 12,
                                    color: '#545151',
                                    textAlign: 'center',
                                }}>It is estimated based on your address and the size of your house.</div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Container>
                <div className='d-flex align-items-center gap-3'>
                    <img src="/images/icon_analysis_report.png" alt="" width={30} />
                    <div className={css`
                    font-size:30px;
                    font-weight:700;
                    color:#FCC068E0;
                    padding:24px 0;
                    @media(max-width:990px){
                        font-size:20px;
                    }
                `}>Your Analysis Report</div>
                </div>
            </Container>

            <Container>
                <Row className={css`
                    margin-top:20px;
                    @media(max-width:990px){
                        gap:20px;
                    }
                `}>
                    <Col lg={4}>
                        <div className={css`
                            border:1.5px solid #eef0f2;
                            border-radius:8px;
                            height:100%;
                            display:flex;
                            flex-direction:column;
                        `}>
                            <div className='d-flex flex-column gap-3' style={{ padding: 20 }}>
                                <div className='d-flex align-items-center gap-3'>
                                    <img src="/images/icon_leaf3.png" alt="" width={30} />
                                    <CardTitle>Your Proportion Of Green Energy</CardTitle>
                                </div>
                                <div style={{ width: '65%', margin: '0 auto' }}>
                                    <CircleProgressBar percentage={renewable.renewable_content || 0} />
                                    <div style={{ fontSize: 14, textAlign: 'center' }}>Carbon Emission per month <br />{(((renewable.total - renewable.renewable) || 0)).toFixed(0)} Kwh</div>
                                </div>
                            </div>
                            <div style={{ background: '#f6f6f6', padding: '10px 35px', borderRadius: '0 0 8px 8px', flex: 1 }}>
                                <div style={{ fontWeight: 700 }}>Relatively low. 🌿📉</div>
                                <div >Reduce your carbon emissions by switching to a green plan! 💚🌱🌞</div>
                            </div>
                        </div>
                    </Col>

                    <Col lg={8}>
                        <Card border>
                            <div className='d-flex flex-column gap-3'>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <div className='d-flex gap-3 align-items-center'>
                                        <img src="/images/icon_circulate.png" alt="" width={30} />
                                        <CardTitle>Carbon Emission</CardTitle>
                                    </div>
                                    <div className='d-flex flex-column gap-1'>
                                        <LegendPointTxt color='#FCC068'>Total Electricity (30 Day)</LegendPointTxt>
                                        <LegendPointTxt color='#1DB461'>Green Energy (30 Day)</LegendPointTxt>
                                    </div>
                                </div>

                                <div id="usage-chart" style={{ height: 290 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        {chartLoading ? <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><LoadingRow></LoadingRow></div> :
                                            <LineChart
                                                width={500}
                                                height={300}
                                                data={emissionData}
                                                margin={{
                                                    top: 5,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="time" tick={{ fontSize: 11, fill: '#999' }} stroke="#fff" />
                                                <YAxis tick={{ fontSize: 11, fill: '#999' }} stroke="#fff" />
                                                <Tooltip />
                                                <Line type="monotone" dataKey="total" stroke="#FCC068" />
                                                <Line type="monotone" dataKey="renewable" stroke="#1DB461" />
                                            </LineChart>}
                                    </ResponsiveContainer>
                                </div>

                                <div style={{ textAlign: 'center', fontSize: 14, color: '#AFAFAF', marginTop: 10 }}>Based on your actual electricity consumption and the real situation in your nearby area is estimated.<br />
                                    Your can click {' '}
                                    <a
                                        style={{
                                            color: '#007bff',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => { setShowDataSourceModal(true) }}>
                                        'Get My Usage'
                                    </a>{' '} below the address to obtain real data.</div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Container>
                <div className='d-flex gap-3 align-items-center mt-3'>
                    <img src="/images/icon_smile.png" alt="" width={30} height={30} />
                    <div style={{
                        fontSize: 20,
                        padding: '24px 0'
                    }}><span style={{
                        color: '#FCC068E0',
                        fontWeight: 700,
                        marginRight: 10
                    }}>Did You Know?</span>Reducing 1 ton of carbon emissions is equivalent to...</div>
                </div>
            </Container>

            <Container className='mb-5'>
                <Row xs={1} md={2} lg={3} >
                    {equivalentCards.map((item, index) => {
                        return <Col key={index} style={{ padding: '10px' }}>
                            <Card border color={index % 2 ? '#fff' : '#f6f6f6'}>
                                <div className='d-flex flex-column gap-2' >
                                    <div style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: '#3c3737'
                                    }}>{item.title}</div>
                                    <div style={{
                                        fontSize: 20,
                                        color: '#3c3737'
                                    }}>{item.imgTxt}</div>
                                    <div style={{
                                        fontSize: 12,
                                        color: '999'
                                    }}>{item.intro}</div>
                                </div>
                            </Card>
                        </Col>
                    })}
                </Row>
            </Container>

            <Container>
                <div className='d-flex gap-3 align-items-center mt-3'>
                    <img src="/images/icon_reduce_carbon.png" alt="" width={30} height={30} />
                    <div style={{
                        fontSize: 20,
                        padding: '24px 0'
                    }}><span style={{
                        color: '#FCC068E0',
                        fontWeight: 700,
                        marginRight: 10
                    }}>Ways to Reduce Carbon Emissions Include</span></div>
                </div>
            </Container>

            <Container className='mb-5'>
                <Row xs={1} md={2} lg={3} >
                    {reduceCards.map((item, index) => {
                        return <Col key={index} style={{ padding: '10px' }}>
                            <Card color={item.bkColor} style={{
                                backgroundImage: `url(/images/home_reduce_img${index}.png)`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                            }}>
                                <div className='d-flex flex-column justify-content-between' style={{ height: '100%' }}>
                                    <div className='d-flex flex-column gap-2' >
                                        <div className='d-flex justify-content-between mb-2'>
                                            <div className={css`
                                                font-size:12px;
                                                color:#164122;
                                                font-weight:700;
                                            `}>Carbon avoided</div>
                                            <div className={css`
                                                font-size:14px;
                                                color:#545151;
                                                font-weight:700;
                                                background:#E1F6EF;
                                                border-radius:7px;
                                                padding:3px 12px;
                                            `}>{index === 0 ? `↓-${removal.toFixed(2)}%` : index === 4 ? `↓-${(100 - renewable.renewable_content).toFixed(2)}%` : item.titleTag}</div>
                                        </div>

                                        <div className={css`
                                            font-size:18px;
                                            color:#545151;
                                            font-weight:700;
                                        `}>{item.title}</div>

                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: index === 4 ?
                                                    `Avoided <span>${((saveCarbon?.carbon_kg || 0) / 1000).toFixed(2)}</span> tons of CO2 per year🌍 which would taken <span>${saveCarbon?.carbon_tree.toFixed(1) || 0}</span> trees🌲 one year to absorb 🌳❤️.We're committed to continuing our eco-friendly practices and making a meaningful difference for future generations 💪`
                                                    : index === 0 ?
                                                        `To decrease carbon emissions, reduce electricity usage during peak hours when electricity providers increase their supply. Opt for natural ventilation and sunlight during hot daytime hours or use high-efficiency electric appliances.<span>We have noticed that your electricity usage tends to be higher at ${convertTo12HourFormat(maxhourly)} every day, so reducing usage during this peak hour can help lower your carbon footprint and energy bill.</span>`
                                                        : item.txt
                                            }}
                                            className={css`
                                            font-size:15px;
                                            color:#999999;
                                            span{
                                                font-style:italic;
                                                color:#EE7517;
                                            }
                                        `} />
                                    </div>
                                    {index === 4 && <div
                                        className={css`
                                            text-align:end;
                                        `}
                                    >
                                        <div>
                                            <Link
                                                to={`javascript:;`}
                                                className={css`
                                                color:#BFDFC2;
                                                font-weight:700;
                                                margin-bottom:0;
                                                text-decoration:none;
                                            `}
                                                onClick={toEnroll}
                                            >[Enroll Now]</Link>
                                        </div>
                                        <Link to={`/plans?address=${encodeURIComponent(`${address}`)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&utcode=${utcode}&usagefrom=smt&actualusage=${fetchActualusage || actualusage}`} className={css`
                                            color:#BFDFC2;
                                            font-size:15px;
                                            text-decoration:underline;
                                        `}>View all 100% green plans</Link>
                                    </div>}
                                </div>
                            </Card>
                        </Col>
                    })}
                </Row>
            </Container>

            <ChooseDataSourceModal
                show={showDataSourceModal}
                handleClose={() => { setShowDataSourceModal(false); }}
            />
            <Footer />
        </>
    )
}

interface TextProps {
    color?: string;
}


const CardTitle = styled.div`
    font-weight:600;
`
const LegendPointTxt = styled.div<TextProps>`
    font-size:12px;
    color: ${props => props.color};
    margin-right:30px;
    white-space:nowrap;
    &:before{
        content:'';
        display:inline-block;
        width:10px;
        height:10px;
        background: ${props => props.color};
        margin-right:5px;
    }
`

export default AnalysisReport